import { Check } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { JOURNEY_STEPS, journeySteps, phoneStepNumber, stepPickable, type JourneyStep, type LeadJourneyFacts } from './leadJourneyModel';

/**
 * P-51 (D-224 s.3) - the lead's road: seven circles, ✓ for a done step, the current one stressed,
 * the rest grey. A view of the progress, not a status picker (D-225 QX-443). On a phone one line:
 * "שלב N מתוך 7 · שם". The same bar sits on the customer's characterization card (s.1).
 */
export default function LeadStageBar({ facts, className = '', onPick }: {
  facts: LeadJourneyFacts;
  className?: string;
  /** P-55b (D-230 s.1): when given, the circles of steps 1-4 are buttons ("להעביר לשלב X?" is the caller's). */
  onPick?: (step: JourneyStep) => void;
}) {
  const { t } = useTranslation();
  const steps = journeySteps(facts);
  const n = phoneStepNumber(facts);
  const allDone = steps.every((s) => s.state === 'done');
  const currentKey = JOURNEY_STEPS[n - 1];
  return (
    <div className={className}>
      <p className="sm:hidden text-sm text-surface-700" data-testid="lead-stage-phone">
        {allDone
          ? t('leads.journey.allDone')
          : t('leads.journey.stepOf', { n, total: JOURNEY_STEPS.length, name: t(`leads.journey.steps.${currentKey}`) })}
      </p>
      <ol className="hidden sm:flex items-start w-full" aria-label={t('leads.journey.ariaLabel')}>
        {steps.map((s, i) => {
          const done = s.state === 'done';
          const current = s.state === 'current';
          const label = t(`leads.journey.steps.${s.key}`);
          return (
            <li key={s.key} className="flex items-start flex-1 last:flex-initial min-w-0" aria-current={current ? 'step' : undefined}>
              <div className="flex flex-col items-center gap-1.5 min-w-0 max-w-[7rem]">
                {(() => {
                  const pickable = !!onPick && stepPickable(facts, s.key);
                  const cls = `w-7 h-7 rounded-full flex items-center justify-center text-xs font-medium tabular-nums
                    ${done ? 'bg-primary-100 text-primary-700' : ''}
                    ${current ? 'bg-accent-hover text-white ring-4 ring-primary-100' : ''}
                    ${!done && !current ? 'bg-surface-0 border border-surface-300 text-surface-500' : ''}`;
                  const inner = done ? <Check size={14} strokeWidth={2.5} /> : i + 1;
                  return pickable ? (
                    <button
                      type="button"
                      className={`${cls} cursor-pointer hover:ring-2 hover:ring-primary-200 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2`}
                      aria-label={t('leads.journey.moveTo', { name: label })}
                      title={t('leads.journey.moveTo', { name: label })}
                      data-testid={`lead-stage-pick-${s.key}`}
                      onClick={() => onPick!(s.key)}
                    >
                      {inner}
                    </button>
                  ) : (
                    <span className={cls} aria-hidden="true">{inner}</span>
                  );
                })()}
                <span className={`text-xs text-center leading-tight ${current ? 'text-surface-900 font-medium' : done ? 'text-surface-700' : 'text-surface-500'}`}>
                  {label}
                  <span className="sr-only">
                    {' - '}{t(done ? 'leads.journey.stateDone' : current ? 'leads.journey.stateCurrent' : 'leads.journey.stateTodo')}
                  </span>
                </span>
              </div>
              {i < steps.length - 1 && (
                <span aria-hidden="true" className={`flex-1 h-px mt-3.5 mx-1.5 ${done ? 'bg-primary-300' : 'bg-surface-200'}`} />
              )}
            </li>
          );
        })}
      </ol>
    </div>
  );
}
